/**
 * Tema (claro, escuro, cinza, do sistema — HU-012) e marca própria do tenant (HU-007).
 * O tema "do sistema" segue o prefers-color-scheme pelo CSS; os outros fixam data-theme. O cinza é neutro e de
 * contraste médio, para turnos longos de NOC.
 */
export type ThemePref = 'system' | 'light' | 'dark' | 'gray';
export const THEMES: readonly ThemePref[] = ['system', 'light', 'dark', 'gray'];
const KEY = 'hive.theme';

export function isTheme(v: unknown): v is ThemePref {
  return v === 'system' || v === 'light' || v === 'dark' || v === 'gray';
}

export function storedTheme(): ThemePref {
  try {
    const v = window.localStorage.getItem(KEY);
    return isTheme(v) ? v : 'system';
  } catch {
    return 'system';
  }
}

export function applyTheme(t: ThemePref) {
  const root = document.documentElement;
  if (t === 'system') root.removeAttribute('data-theme');
  else root.setAttribute('data-theme', t);
  try {
    window.localStorage.setItem(KEY, t);
  } catch {
    /* sem armazenamento: o tema vale só nesta página */
  }
}

export function nextTheme(t: ThemePref): ThemePref {
  return THEMES[(THEMES.indexOf(t) + 1) % THEMES.length] ?? 'system';
}

// ---------------------------------------------------------------- cores e contraste (WCAG)

export function hexToRgb(hex: string): [number, number, number] {
  let h = hex.replace('#', '').trim();
  if (h.length === 3) h = h.split('').map((c) => c + c).join('');
  const n = parseInt(h, 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

function rgbToHex(rgb: readonly number[]): string {
  return '#' + rgb.map((v) => Math.max(0, Math.min(255, Math.round(v))).toString(16).padStart(2, '0')).join('').toUpperCase();
}

function luminance(rgb: readonly number[]): number {
  const c = rgb.map((v) => {
    const s = v / 255;
    return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
  });
  return 0.2126 * (c[0] ?? 0) + 0.7152 * (c[1] ?? 0) + 0.0722 * (c[2] ?? 0);
}

export function contrast(a: string, b: string): number {
  const la = luminance(hexToRgb(a));
  const lb = luminance(hexToRgb(b));
  return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
}

export function isHexColor(v: unknown): v is string {
  return typeof v === 'string' && /^#[0-9a-fA-F]{6}$/.test(v);
}

/** Mistura a cor com outra (0 = original, 1 = a outra). */
export function mix(hex: string, other: string, amount: number): string {
  const a = hexToRgb(hex);
  const b = hexToRgb(other);
  return rgbToHex(a.map((v, i) => v + ((b[i] ?? 0) - v) * amount));
}

/** Variação mais próxima que atende AA (4,5:1) com texto branco, escurecendo aos poucos. */
export function accessibleVariant(hex: string, against = '#FFFFFF', min = 4.5): string {
  let c = hex.toUpperCase();
  for (let i = 0; i < 30 && contrast(c, against) < min; i++) c = mix(c, '#000000', 0.08);
  return c;
}

/**
 * Aplica a cor principal da marca nos tokens de acento, nos dois temas.
 * No escuro usa uma versão clareada para manter o contraste sobre fundos escuros.
 */
export function applyBrandColor(hex: string | null | undefined) {
  const id = 'hive-brand-style';
  let el = document.getElementById(id) as HTMLStyleElement | null;
  if (!isHexColor(hex)) {
    el?.remove();
    if (brandSheet) document.adoptedStyleSheets = document.adoptedStyleSheets.filter((x) => x !== brandSheet);
    return;
  }
  // texto na cor da marca aparece sobre o branco, o fundo da página e a linha com o mouse: confere contra o mais
  // escuro deles (--surface-2), que garante os outros; no escuro, contra a superfície mais clara em que aparece
  const light = accessibleVariant(hex, '#EEF2F0');
  const lightVars = `--accent:${light};--accent-strong:${mix(light, '#000000', 0.25)};--accent-soft:${mix(light, '#FFFFFF', 0.86)};--focus:${light};--accent-fg:#FFFFFF;--s1:${light};`;
  let dark = mix(hex, '#FFFFFF', 0.45);
  for (let i = 0; i < 20 && contrast(dark, '#1D2623') < 4.5; i++) dark = mix(dark, '#FFFFFF', 0.1);
  const darkVars = `--accent:${dark};--accent-strong:${mix(dark, '#FFFFFF', 0.3)};--accent-soft:${mix(dark, '#161D1B', 0.78)};--focus:${dark};--accent-fg:#0B1A16;--s1:${dark};`;
  // cinza: confere contra a superfície mais clara em que o texto do acento aparece (--surface-3)
  let gray = mix(hex, '#FFFFFF', 0.45);
  for (let i = 0; i < 20 && contrast(gray, GRAY_SURFACE_3) < 4.5; i++) gray = mix(gray, '#FFFFFF', 0.1);
  const grayVars = `--accent:${gray};--accent-strong:${mix(gray, '#FFFFFF', 0.3)};--accent-soft:${mix(gray, '#323438', 0.78)};--focus:${gray};--accent-fg:#111722;--s1:${gray};`;
  // trilho (barra de ícones à esquerda): tom bem escuro da cor da marca, igual nos temas; texto claro com contraste AA
  const { bg: railBg, fg: railFg } = railColors(hex);
  const railVars = `--rail-bg:${railBg};--rail-hover:${mix(railBg, '#FFFFFF', 0.12)};--rail-fg:${railFg};`;
  const css =
    `:root{${lightVars}${railVars}}` +
    `@media (prefers-color-scheme: dark){:root:not([data-theme="light"]):not([data-theme="gray"]){${darkVars}${railVars}}}` +
    `:root[data-theme="dark"]{${darkVars}${railVars}}` +
    `:root[data-theme="gray"]{${grayVars}${railVars}}`;
  // folha construída: a política de conteúdo da console não aceita <style> criado por script
  if (typeof CSSStyleSheet !== 'undefined' && 'adoptedStyleSheets' in Document.prototype && 'replaceSync' in CSSStyleSheet.prototype) {
    el?.remove();
    brandSheet ??= new CSSStyleSheet();
    brandSheet.replaceSync(css);
    if (!document.adoptedStyleSheets.includes(brandSheet)) document.adoptedStyleSheets = [...document.adoptedStyleSheets, brandSheet];
    return;
  }
  if (!el) {
    el = document.createElement('style');
    el.id = id;
    document.head.appendChild(el);
  }
  el.textContent = css;
}

/**
 * Cores do trilho de ícones para a cor da marca: perto da cor escolhida (só escurece o quanto precisa para o texto
 * branco ter contraste alto), não um quase-preto; texto claro com contraste AA. Usada também na prévia da marca.
 */
export function railColors(hex: string): { bg: string; fg: string; hover: string } {
  let bg = mix(hex, '#000000', 0.25);
  for (let i = 0; i < 20 && contrast('#FFFFFF', bg) < 7; i++) bg = mix(bg, '#000000', 0.08);
  let fg = mix(hex, '#FFFFFF', 0.85);
  for (let i = 0; i < 20 && contrast(fg, bg) < 4.5; i++) fg = mix(fg, '#FFFFFF', 0.3);
  return { bg, fg, hover: mix(bg, '#FFFFFF', 0.12) };
}

let brandSheet: CSSStyleSheet | null = null;
/** --surface-3 do tema cinza (tokens.css): a superfície mais clara dele. */
const GRAY_SURFACE_3 = '#43464B';
